// /atendimento — chat público do site com o Dom (A22). Aparece dentro do site da 3S pelo widget (iframe).
import { useEffect, useRef, useState } from 'react';

interface Msg {
  role: 'dom' | 'visitante';
  text: string;
}
const KEY = 'mcc-chat-site';

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json', 'x-requested-with': 'mcc' }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.message ?? 'Atendimento indisponível no momento.');
  return data as T;
}

export function SiteChat() {
  const [session, setSession] = useState<{ sessionId: string; token: string } | null>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) ?? 'null');
    } catch {
      return null;
    }
  });
  const [msgs, setMsgs] = useState<Msg[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`${KEY}-msgs`) ?? '[]');
    } catch {
      return [];
    }
  });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (session) return;
    const page = new URLSearchParams(location.search).get('page') ?? document.referrer ?? undefined;
    post<{ sessionId: string; token: string; greeting: string }>('/api/public/chat/start', { pageUrl: page?.slice(0, 300) })
      .then((r) => {
        setSession({ sessionId: r.sessionId, token: r.token });
        setMsgs([{ role: 'dom', text: r.greeting }]);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  useEffect(() => {
    try {
      if (session) sessionStorage.setItem(KEY, JSON.stringify(session));
      sessionStorage.setItem(`${KEY}-msgs`, JSON.stringify(msgs.slice(-60)));
    } catch {
      /* sem armazenamento: a conversa segue só nesta tela */
    }
    end.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session, msgs]);

  const send = async () => {
    const t = text.trim();
    if (!t || !session || busy) return;
    setMsgs((m) => [...m, { role: 'visitante', text: t }]);
    setText('');
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ reply: string }>('/api/public/chat/message', { ...session, text: t });
      setMsgs((m) => [...m, { role: 'dom', text: r.reply }]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="flex items-center gap-3 bg-brand-900 px-4 py-3 text-white">
        <img src="/brand/3s-logo-negativo.svg" alt="3S Networks" className="h-8 w-auto" />
        <div>
          <p className="text-sm font-semibold">Dom · 3S Networks</p>
          <p className="text-xs text-accent-400">Assistente virtual com IA</p>
        </div>
      </header>
      <main className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-3" aria-live="polite">
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3 py-2 text-sm ${m.role === 'dom' ? 'bg-white text-slate-900 shadow-sm' : 'ml-auto bg-brand-900 text-white'}`}>
            {m.text}
          </div>
        ))}
        {busy && <div className="max-w-[85%] rounded-2xl bg-white px-3 py-2 text-sm text-slate-500 shadow-sm">Dom está digitando…</div>}
        {error && (
          <div role="alert" className="rounded bg-red-50 p-2 text-sm text-red-900">
            {error}
          </div>
        )}
        <div ref={end} />
      </main>
      <form
        className="border-t border-slate-200 p-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <div className="flex gap-2">
          <label htmlFor="chat-msg" className="sr-only">
            Sua mensagem
          </label>
          <input id="chat-msg" className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm" placeholder="Escreva sua mensagem…" maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} disabled={!session} />
          <button type="submit" className="rounded-full bg-brand-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || !session || !text.trim()}>
            Enviar
          </button>
        </div>
        <p className="mt-1 text-center text-[11px] text-slate-500">
          Atendimento com IA. Seus dados são usados só para contato, conforme a{' '}
          <a href="https://www.3snetworks.com.br/privacidade" target="_blank" rel="noopener noreferrer" className="underline">
            política de privacidade
          </a>
          .
        </p>
      </form>
    </div>
  );
}
